import { useSearch } from "@tanstack/react-router";
import { usePostHog } from "posthog-js/react";
import { useCallback, useMemo, useRef } from "react";
import { env } from "../config/env";
import { useRampsStore } from "../stores/use-ramps-store";
import type {
	AnalyticsEvent,
	BaseAnalyticsProperties,
	EntryEventProperties,
	ExitEventProperties,
	PageViewProperties,
} from "../utils/analytics";

/**
 * Custom hook to access PostHog analytics
 * Provides a clean interface for tracking events throughout the application
 * Automatically enriches events with environment, wallet_provider, session metadata
 */
export function useAnalytics() {
	const posthog = usePostHog();
	const search = useSearch({ strict: false });
	const store = useRampsStore();

	// Track page entry time for calculating time on page
	const pageEntryTimeRef = useRef<number>(Date.now());
	const previousPageRef = useRef<string | null>(null);

	// Get common properties that are enriched in all events
	const commonProperties = useMemo((): BaseAnalyticsProperties => {
		const searchParams = search as Record<string, unknown>;

		return {
			// Environment context
			environment: env.TARGET_ENV,
			api_version: store.apiVersion || "v3",

			// Session context
			session_id: posthog?.get_session_id?.() || undefined,
			timestamp: Date.now(),

			// User journey context
			page_path:
				typeof window !== "undefined" ? window.location.pathname : undefined,
			current_step:
				(searchParams?.step as string) || store.currentStep || undefined,
			wallet_provider: (searchParams?.wallet_provider as string) || undefined,

			// Device context
			user_agent:
				typeof navigator !== "undefined" ? navigator.userAgent : undefined,
			language: (searchParams?.lang as string) || "en",

			// Referrer context
			referrer: typeof document !== "undefined" ? document.referrer : undefined,
		};
	}, [search, store.apiVersion, store.currentStep, posthog]);

	/**
	 * Track a custom event with automatic property enrichment
	 */
	const trackEvent = useCallback(
		(
			eventName: AnalyticsEvent | string,
			properties?: BaseAnalyticsProperties,
		) => {
			if (posthog) {
				// Merge common properties with event-specific properties
				const enrichedProperties = {
					...commonProperties,
					...properties,
				};

				posthog.capture(eventName, enrichedProperties);
			}
		},
		[posthog, commonProperties],
	);

	/**
	 * Track page view with enhanced metadata
	 */
	const trackPageView = useCallback(
		(pageName: string, additionalProps?: Partial<PageViewProperties>) => {
			if (posthog) {
				const now = Date.now();
				const timeOnPreviousPage = now - pageEntryTimeRef.current;

				const pageViewProperties: PageViewProperties = {
					...commonProperties,
					page_name: pageName,
					page_title:
						typeof document !== "undefined" ? document.title : undefined,
					previous_page: previousPageRef.current || undefined,
					time_on_previous_page_ms: previousPageRef.current
						? timeOnPreviousPage
						: undefined,
					...additionalProps,
				};

				posthog.capture("$pageview", {
					$current_url:
						typeof window !== "undefined" ? window.location.href : undefined,
					...pageViewProperties,
				});

				// Update refs for next page view
				previousPageRef.current = pageName;
				pageEntryTimeRef.current = now;
			}
		},
		[posthog, commonProperties],
	);

	/**
	 * Track session start with entry metadata
	 */
	const trackSessionStart = useCallback(
		(entryMetadata: Partial<EntryEventProperties>) => {
			if (posthog) {
				const searchParams = search as Record<string, unknown>;

				// Detect if running in WebView
				const userAgent =
					typeof navigator !== "undefined" ? navigator.userAgent : "";
				const isWebView =
					/WebView|wv/.test(userAgent) ||
					(typeof window !== "undefined" &&
						(window as typeof window & { ReactNativeWebView?: unknown })
							.ReactNativeWebView !== undefined);

				const webviewPlatform = isWebView
					? /iPhone|iPad|iPod/.test(userAgent)
						? "ios"
						: /Android/.test(userAgent)
							? "android"
							: "unknown"
					: undefined;

				const entryProperties: EntryEventProperties = {
					...commonProperties,
					wallet_provider:
						(searchParams?.wallet_provider as string) || "unknown",
					has_token: !!(searchParams?.token || searchParams?.accesstoken),
					has_transaction_id: !!searchParams?.transaction_id,
					operation:
						(searchParams?.operation as "withdraw" | "deposit") || "withdraw",
					asset_code: (searchParams?.asset_code as string) || "USDC",
					is_webview: isWebView,
					webview_platform: webviewPlatform,
					entry_source: isWebView
						? "webview"
						: document.referrer
							? "referral"
							: "direct",
					initial_landing_page:
						typeof window !== "undefined"
							? window.location.pathname
							: undefined,
					...entryMetadata,
				};

				posthog.capture("session_started", entryProperties);
			}
		},
		[posthog, commonProperties, search],
	);

	/**
	 * Track user exit from the application
	 */
	const trackExit = useCallback(
		(
			exitType: ExitEventProperties["exit_type"],
			exitMetadata?: Partial<ExitEventProperties>,
		) => {
			if (posthog) {
				const sessionStartTime = pageEntryTimeRef.current;
				const now = Date.now();
				const timeInSession = now - sessionStartTime;

				const exitProperties: ExitEventProperties = {
					...commonProperties,
					exit_type: exitType,
					last_page:
						typeof window !== "undefined"
							? window.location.pathname
							: undefined,
					time_in_session_ms: timeInSession,
					transaction_id: store.transactionId || undefined,
					transaction_status: store.currentStep || undefined,
					...exitMetadata,
				};

				// Map exit type to specific event
				const eventMap = {
					completed: "transaction_completed",
					cancelled: "transaction_cancelled",
					abandoned: "session_abandoned",
					error: "error_occurred",
				};

				posthog.capture(eventMap[exitType], exitProperties);
			}
		},
		[posthog, commonProperties, store.transactionId, store.currentStep],
	);

	/**
	 * Identify user with properties
	 */
	const identifyUser = useCallback(
		(userId: string, properties?: Record<string, unknown>) => {
			if (posthog) {
				const searchParams = search as Record<string, unknown>;

				// Set user properties with environment context
				const userProperties = {
					environment: env.TARGET_ENV,
					wallet_provider: searchParams?.wallet_provider as string,
					language: (searchParams?.lang as string) || "en",
					api_version: store.apiVersion || "v3",
					...properties,
				};

				posthog.identify(userId, userProperties);
			}
		},
		[posthog, search, store.apiVersion],
	);

	/**
	 * Reset user session
	 */
	const resetUser = useCallback(() => {
		if (posthog) {
			posthog.reset();
		}
	}, [posthog]);

	/**
	 * Set user properties
	 */
	const setUserProperties = useCallback(
		(properties: Record<string, unknown>) => {
			if (posthog) {
				posthog.people.set(properties);
			}
		},
		[posthog],
	);

	return {
		trackEvent,
		trackPageView,
		trackSessionStart,
		trackExit,
		identifyUser,
		resetUser,
		setUserProperties,
		posthog, // Direct access to PostHog instance if needed
	};
}
